import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import { AddSectionButton, AddSectionOverlay, toneStyles } from '@/components/block-primitives';
import type { CompareBlockConfig, CompareColumn, CompareItem } from './artifact-block-types';
import EditableText from './editable-text';
type ArtifactCompareBlockProps = {
  config: CompareBlockConfig;
  editable?: boolean;
  onChange?: (next: CompareBlockConfig) => void;
};
const ArtifactCompareBlock = t0 => {
  const $ = _c(49);
  const {
    config,
    editable: t1,
    onChange
  } = t0;
  const editable = t1 === undefined ? false : t1;
  const [addTitle, setAddTitle] = useState(false);
  const [addNote, setAddNote] = useState(false);
  let t2;
  if ($[0] !== config || $[1] !== onChange) {
    t2 = next => onChange?.({
      ...config,
      ...next
    });
    $[0] = config;
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const patch = t2;
  let t3;
  if ($[3] !== config.columns || $[4] !== patch) {
    t3 = (columnIndex, next_0) => patch({
      columns: config.columns.map((column, i) => i === columnIndex ? {
        ...column,
        ...next_0
      } : column)
    });
    $[3] = config.columns;
    $[4] = patch;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const patchColumn = t3;
  let t4;
  if ($[6] !== config.columns || $[7] !== patchColumn) {
    t4 = (columnIndex_0, itemIndex, next_1) => patchColumn(columnIndex_0, {
      items: config.columns[columnIndex_0].items.map((item, i_0) => i_0 === itemIndex ? {
        ...item,
        ...next_1
      } : item)
    });
    $[6] = config.columns;
    $[7] = patchColumn;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const patchItem = t4;
  const showTitle = !!config.title || addTitle;
  const showNote = !!config.note || addNote;
  let t5;
  if ($[9] !== editable || $[10] !== showTitle) {
    t5 = editable && !showTitle && <AddSectionOverlay position="top"><AddSectionButton label="Add a title" onClick={() => setAddTitle(true)} /></AddSectionOverlay>;
    $[9] = editable;
    $[10] = showTitle;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== editable || $[13] !== showNote) {
    t6 = editable && !showNote && <AddSectionOverlay position="bottom"><AddSectionButton label="Add a note" onClick={() => setAddNote(true)} /></AddSectionOverlay>;
    $[12] = editable;
    $[13] = showNote;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = config.title || "Comparison";
  let t8;
  if ($[15] !== addTitle || $[16] !== config.title || $[17] !== editable || $[18] !== patch || $[19] !== showTitle) {
    t8 = showTitle && <EditableText autoFocus={addTitle} block={true} className="text-label-2 text-primary" editable={editable} label="Comparison title" onSubmit={title => patch({
      title
    })} onEmptyBlur={() => setAddTitle(false)} optional={true} placeholder="Add a title" value={config.title ?? ""} />;
    $[15] = addTitle;
    $[16] = config.title;
    $[17] = editable;
    $[18] = patch;
    $[19] = showTitle;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== config.columns || $[22] !== editable || $[23] !== patchColumn || $[24] !== patchItem) {
    let t10;
    if ($[26] !== editable || $[27] !== patchColumn || $[28] !== patchItem) {
      t10 = (column_0, columnIndex_1) => <div className="min-w-0 rounded-lg border border-tertiary p-4" key={columnIndex_1}><div className="mb-3 flex items-start gap-2 border-b border-tertiary pb-2.5"><span aria-hidden="true" className="flex h-5 shrink-0 items-center"><span className={cx("h-2.5 w-2.5 rounded-xs", toneStyles[column_0.tone ?? "neutral"].marker)} /></span><EditableText className="break-words text-label-2 text-primary" editable={editable} label="Column title" onSubmit={title_0 => patchColumn(columnIndex_1, {
            title: title_0
          })} value={column_0.title} /></div><dl className="flex flex-col gap-3">{column_0.items.map((item_0, itemIndex_0) => <div className="flex flex-col gap-0.5" key={itemIndex_0}><EditableText as="dt" className="min-w-0 break-words text-caption-2 text-tertiary" editable={editable} label="Row label" onSubmit={label => patchItem(columnIndex_1, itemIndex_0, {
              label
            })} value={item_0.label} /><EditableText as="dd" className="min-w-0 break-words text-label-2 tabular-nums text-primary" editable={editable} label="Row value" onSubmit={value => patchItem(columnIndex_1, itemIndex_0, {
              value
            })} value={item_0.value} /></div>)}</dl></div>;
      $[26] = editable;
      $[27] = patchColumn;
      $[28] = patchItem;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    t9 = config.columns.map(t10);
    $[21] = config.columns;
    $[22] = editable;
    $[23] = patchColumn;
    $[24] = patchItem;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[30] !== t9) {
    t10 = <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,240px),1fr))] gap-6">{t9}</div>;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10 || $[33] !== t8) {
    t11 = <div className="flex flex-col gap-4 px-4 py-4 md:px-6 md:py-5">{t8}{t10}</div>;
    $[32] = t10;
    $[33] = t8;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== addNote || $[36] !== config.note || $[37] !== editable || $[38] !== patch || $[39] !== showNote) {
    t12 = showNote && <div className="border-t border-tertiary bg-secondary px-4 md:px-6 py-3 text-caption-4 text-tertiary"><EditableText autoFocus={addNote} block={true} editable={editable} label="Comparison note" onSubmit={note => patch({
        note
      })} onEmptyBlur={() => setAddNote(false)} optional={true} placeholder="Add a note" value={config.note ?? ""} /></div>;
    $[35] = addNote;
    $[36] = config.note;
    $[37] = editable;
    $[38] = patch;
    $[39] = showNote;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t11 || $[42] !== t12 || $[43] !== t7) {
    t13 = <section aria-label={t7} className="overflow-hidden rounded-lg border border-tertiary bg-primary">{t11}{t12}</section>;
    $[41] = t11;
    $[42] = t12;
    $[43] = t7;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== t13 || $[46] !== t5 || $[47] !== t6) {
    t14 = <div className="group relative">{t5}{t6}{t13}</div>;
    $[45] = t13;
    $[46] = t5;
    $[47] = t6;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  return t14;
};
export default ArtifactCompareBlock;